/**
 * API конструктора шаблонов карточек ГО.
 */

import { del, get, post, put } from "@/lib/api-client-v2";
import type {
  UdgspzCardTemplateListResponse,
  UdgspzCardTemplateWithDetails,
  UdgspzCardFieldType,
} from "@/types/udgspz-card-template";
import type {
  CardFieldWidgetTypeItem,
  CardTemplatePublishRequest,
  CardTemplatePublishResponse,
  CardTemplateRestoreRequest,
} from "@/types/card-template";

export interface UdgspzCardTemplateCreatePayload {
  code: string;
  name: string;
  description?: string | null;
  eudg_type_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
}

export interface UdgspzCardTemplateUpdatePayload {
  name?: string;
  description?: string | null;
  eudg_type_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface UdgspzCardSectionPayload {
  code: string;
  name: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface UdgspzCardSectionUpdatePayload {
  code?: string;
  name?: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface UdgspzCardFieldPayload {
  field_key: string;
  label: string;
  description?: string | null;
  section_id?: number | null;
  field_type: UdgspzCardFieldType;
  qual_id: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export interface UdgspzCardFieldUpdatePayload {
  field_key?: string;
  label?: string;
  description?: string | null;
  section_id?: number | null;
  field_type?: UdgspzCardFieldType;
  qual_id?: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export const udgspzCardTemplatesApi = {
  listFieldWidgetTypes: () =>
    get<CardFieldWidgetTypeItem[]>("/udgspz-card-templates/meta/field-widget-types"),

  list: (params?: {
    status?: string;
    eudg_type_kls_id?: number;
    global_type_only?: boolean;
    is_active?: boolean;
    skip?: number;
    limit?: number;
  }) => get<UdgspzCardTemplateListResponse>("/udgspz-card-templates", params ?? {}),

  get: (id: number) => get<UdgspzCardTemplateWithDetails>(`/udgspz-card-templates/${id}`),

  create: (body: UdgspzCardTemplateCreatePayload) =>
    post<UdgspzCardTemplateWithDetails>("/udgspz-card-templates", body),

  update: (id: number, body: UdgspzCardTemplateUpdatePayload) =>
    put<UdgspzCardTemplateWithDetails>(`/udgspz-card-templates/${id}`, body),

  publish: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/udgspz-card-templates/${id}/publish`, body ?? {}),

  archive: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/udgspz-card-templates/${id}/archive`, body ?? {}),

  restore: (id: number, body?: CardTemplateRestoreRequest) =>
    post<CardTemplatePublishResponse>(`/udgspz-card-templates/${id}/restore`, body ?? {}),

  delete: (id: number) => del<void>(`/udgspz-card-templates/${id}`),

  clone: (templateId: number, newCode: string) =>
    post<UdgspzCardTemplateWithDetails>(
      `/udgspz-card-templates/${templateId}/clone?new_code=${encodeURIComponent(newCode)}`,
      {},
    ),

  addSection: (templateId: number, body: UdgspzCardSectionPayload) =>
    post<UdgspzCardTemplateWithDetails>(`/udgspz-card-templates/${templateId}/sections`, body),

  updateSection: (templateId: number, sectionId: number, body: UdgspzCardSectionUpdatePayload) =>
    put<UdgspzCardTemplateWithDetails>(`/udgspz-card-templates/${templateId}/sections/${sectionId}`, body),

  deleteSection: (templateId: number, sectionId: number) =>
    del<UdgspzCardTemplateWithDetails>(`/udgspz-card-templates/${templateId}/sections/${sectionId}`),

  addField: (templateId: number, body: UdgspzCardFieldPayload) =>
    post<UdgspzCardTemplateWithDetails>(`/udgspz-card-templates/${templateId}/fields`, body),

  updateField: (templateId: number, fieldId: number, body: UdgspzCardFieldUpdatePayload) =>
    put<UdgspzCardTemplateWithDetails>(`/udgspz-card-templates/${templateId}/fields/${fieldId}`, body),

  deleteField: (templateId: number, fieldId: number) =>
    del<UdgspzCardTemplateWithDetails>(`/udgspz-card-templates/${templateId}/fields/${fieldId}`),
};
